import { useCallback } from 'react';
import {
    StructureSetCopyService,
    CopyStructureSetResult,
    CopyToTargetParams,
    SdfContourResolutionStrategy,
} from '~/domain/services/structureSetService/structureSetCopyService';
import { StructureSet } from '~/domain/patient/structureset';
import { Image3D } from '~/domain/patient/image3d';
import { Patient } from '~/domain/patient/patient';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { ensureStructureSetsCurrent } from '~/hooks/ensureStructureSetsCurrent';

export function useCopyStructureSet() {
    // Resolved through the edit target: the grid Environment that shows the
    // active set holds its current contours, which the copy takes as the
    // source.
    const { environment } = useActiveEditTarget();
    const session = useSessionOrNull();

    const canCopy = useCallback(
        (
            sourceStructureSet: StructureSet | null | undefined,
            targetImage: Image3D | null | undefined
        ): boolean => {
            if (!sourceStructureSet || !targetImage) {
                return false;
            }
            if (!sourceStructureSet.image) {
                return false;
            }
            if (!targetImage.frameOfReferenceUid) {
                return false;
            }
            if (sourceStructureSet.image.seriesUid === targetImage.seriesUid) {
                return false;
            }
            return true;
        },
        []
    );

    /**
     * Single entry point for the copy dialog: lets the domain service pick the
     * copy variant, while this hook supplies the framework-bound concerns
     * (engine snapshot, SDF contour strategy, and registering a newly created
     * structure set on the patient). Into-existing copies mutate the target in
     * place and are not re-registered.
     *
     * Resolves null, copying nothing, when the source or the target set lost
     * contour edits to a failed switch-away flush: its domain copy is stale,
     * and the user has been told which set.
     */
    const copyToTarget = useCallback(
        async (
            params: CopyToTargetParams,
            patient: Patient
        ): Promise<CopyStructureSetResult | null> => {
            if (!environment) {
                throw new Error(
                    'Environment is not available for copying structure set.'
                );
            }

            const copiesIntoExisting = params.targetStructureSet !== undefined;

            // Copies that create a new structure set need the source image to
            // resolve geometry; into-existing copies remap onto the target.
            if (!copiesIntoExisting && !params.sourceStructureSet.image) {
                throw new Error(
                    'Source structure set is not linked to an image. ' +
                        'Cannot determine geometry for copying.'
                );
            }

            // Only the approved into-existing variants that remap contours
            // (registration / same frame of reference) skip the flush, on the
            // assumption an approved source has no pending edits to lose. Every
            // other copy, the plain into-existing path included, snapshots
            // pending engine edits of every registry-held grid Environment
            // before the service reads source geometry, and refuses a source
            // or target whose edits were lost.
            const remapsContours =
                params.registration !== undefined ||
                params.sameFrameOfReference === true;
            const sourceIsApproved =
                params.sourceStructureSet.approvalStatus ===
                DICOMApprovalStatus.APPROVED;
            const preservesApprovedSource =
                copiesIntoExisting && remapsContours && sourceIsApproved;
            if (
                !preservesApprovedSource &&
                !ensureStructureSetsCurrent(
                    getDatasetEnvironmentRegistry(),
                    params.targetStructureSet
                        ? [params.sourceStructureSet, params.targetStructureSet]
                        : [params.sourceStructureSet],
                    'Copy'
                )
            ) {
                return null;
            }

            const service = new StructureSetCopyService();
            const strategy = new SdfContourResolutionStrategy(environment);
            const result = await service.copyToTarget(params, strategy);

            if (!copiesIntoExisting) {
                if (session) {
                    session.context.tracker.batch(() =>
                        patient.addStructureSet(result.structureSet)
                    );
                } else {
                    patient.addStructureSet(result.structureSet);
                }
            } else if (session && result.copiedRoiNumbers.length > 0) {
                // The service mutated the target set in place. Nothing in that
                // path touches the tracker, so without this the target's
                // version never bumps and the copy is invisible to dirty
                // tracking. Marking after the fact (rather than wrapping the
                // copy) is deliberate — copyToTarget is async and command()
                // flushes synchronously, so a wrapping batch would close
                // before the mutations land.
                //
                // Each copied ROI is marked 'created' individually: the
                // wrappers are fresh (syncRoiObjects), and a bare SS-level
                // mark carries no ROI changes, so the pending-changes store
                // would classify the copy as "nothing to save" and the save
                // workflow would silently skip it (ClickUp 869dw4gud).
                const { tracker } = session.context;
                tracker.command(() => {
                    for (const roiNumber of result.copiedRoiNumbers) {
                        const roi = result.structureSet.rois.find(
                            (r) => r.roiNumber === roiNumber
                        );
                        if (roi) {
                            tracker.markChanged(roi, 'created');
                        } else {
                            // A copied ROI without a wrapper means the
                            // sync step is broken; say so rather than
                            // under-report the copy as saved-state clean.
                            console.error(
                                'Copied ROI has no domain wrapper; it will ' +
                                    'not be tracked as an unsaved change'
                            );
                        }
                    }
                    tracker.markChanged(result.structureSet);
                });
            }

            return result;
        },
        [environment, session]
    );

    return {
        copyToTarget,
        canCopy,
    };
}
